import React, { useState, useEffect, useCallback, Fragment } from "react";
import { Pencil, Search, Trash2 } from "lucide-react";
import { dashboardToast } from "../../../utils/dashboardToast";
import ContentLoader from "../../ContentLoader";
import SettingsPanelActions from "./SettingsPanelActions";
import {
  Alert,
  Button,
  Input,
  Modal,
  Switch,
} from "../../ui";
import {
  addOrUpdateSourceType,
  deleteSourceType,
  getSourceTypes,
} from "../../../services/frameworkApi";

const SourceTypesPanel = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({ id: 0, name: "", isActive: true });
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [rowToDelete, setRowToDelete] = useState(null);

  const loadItems = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const types = await getSourceTypes(searchTerm.trim() || "", false);
      setItems(types);
    } catch (err) {
      console.error("Error loading source types:", err);
      setError(err.message || "Failed to load source types.");
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [searchTerm]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      loadItems();
    }, searchTerm ? 300 : 0);
    return () => window.clearTimeout(timer);
  }, [loadItems, searchTerm]);

  const openModal = (row = null) => {
    if (row) {
      setFormData({
        id: row.id,
        name: row.name || "",
        isActive: row.isActive !== false,
      });
    } else {
      setFormData({ id: 0, name: "", isActive: true });
    }
    setShowModal(true);
  };

  const confirmDelete = async () => {
    if (!rowToDelete?.id) return;

    try {
      setLoading(true);
      await deleteSourceType(rowToDelete.id);
      dashboardToast.success("Source type deleted");
      setShowDeleteModal(false);
      setRowToDelete(null);
      await loadItems();
    } catch (err) {
      dashboardToast.error(err.message || "Failed to delete source type");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const name = formData.name.trim();
    if (!name) {
      dashboardToast.error("Source type name is required");
      return;
    }

    try {
      setLoading(true);
      await addOrUpdateSourceType({
        id: formData.id,
        name,
        isActive: formData.isActive,
      });
      dashboardToast.success(formData.id > 0 ? "Source type updated" : "Source type added");
      setShowModal(false);
      await loadItems();
    } catch (err) {
      dashboardToast.error(err.message || "Failed to save source type");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="sa-settings-panel">
      <SettingsPanelActions>
        <div className="dash-search-field dash-table-search">
          <Search className="dash-search-field__icon" aria-hidden />
          <input
            type="text"
            placeholder="Search by name…"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="dash-search-field__input"
            disabled={loading}
          />
        </div>
        <Button variant="primary" className="sa-create-btn" onClick={() => openModal()}>
          Add source type
        </Button>
      </SettingsPanelActions>

      {loading && items.length === 0 ? (
        <ContentLoader label="Loading source types…" />
      ) : error ? (
        <Alert variant="error">{error}</Alert>
      ) : (
        <Fragment>
          <div className="app-table-wrap app-table-wrap--refined">
            <table className="table table-striped">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Status</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {items.length === 0 ? (
                  <tr>
                    <td colSpan={3} className="text-center">
                      <span className="app-td__desc">No source types yet.</span>
                    </td>
                  </tr>
                ) : (
                  items.map((row) => (
                    <tr key={row.id}>
                      <td>
                        <span className="app-td__title">{row.name}</span>
                      </td>
                      <td>
                        <span
                          className={
                            row.isActive !== false
                              ? "app-status app-status--success"
                              : "app-status app-status--muted"
                          }
                        >
                          {row.isActive !== false ? "Active" : "Inactive"}
                        </span>
                      </td>
                      <td>
                        <div className="btn-list-actions">
                          <Button
                            variant="outline"
                            size="sm"
                            className="btn-list-edit"
                            title="Edit"
                            aria-label={`Edit ${row.name}`}
                            onClick={() => openModal(row)}
                          >
                            <Pencil />
                          </Button>
                          <Button
                            variant="danger"
                            size="sm"
                            className="btn-list-delete"
                            title="Delete"
                            aria-label={`Delete ${row.name}`}
                            onClick={() => {
                              setRowToDelete(row);
                              setShowDeleteModal(true);
                            }}
                          >
                            <Trash2 />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </Fragment>
      )}

      <Modal
        open={showModal}
        onClose={() => setShowModal(false)}
        title={formData.id > 0 ? "Edit source type" : "Add source type"}
        footer={
          <>
            <Button variant="outline" type="button" onClick={() => setShowModal(false)}>
              Cancel
            </Button>
            <Button variant="primary" type="submit" form="source-type-settings-form" disabled={loading}>
              {loading ? "Saving…" : "Save"}
            </Button>
          </>
        }
      >
        <form id="source-type-settings-form" className="flex flex-col gap-4" onSubmit={handleSubmit}>
          <Input
            label="Name"
            name="name"
            value={formData.name}
            onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
            required
            placeholder="e.g. Industry framework"
          />
          <Switch
            label="Active"
            checked={formData.isActive}
            onCheckedChange={(checked) => setFormData((prev) => ({ ...prev, isActive: checked }))}
          />
        </form>
      </Modal>

      <Modal
        open={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setRowToDelete(null);
        }}
        title="Delete source type"
        footer={
          <>
            <Button
              variant="outline"
              type="button"
              onClick={() => {
                setShowDeleteModal(false);
                setRowToDelete(null);
              }}
            >
              Cancel
            </Button>
            <Button variant="danger" type="button" onClick={confirmDelete} disabled={loading}>
              {loading ? "Deleting…" : "Delete"}
            </Button>
          </>
        }
      >
        <p className="m-0 text-sm text-[var(--app-text-muted)]">
          Delete &quot;{rowToDelete?.name}&quot;? This cannot be undone.
        </p>
      </Modal>
    </div>
  );
};

export default SourceTypesPanel;
